import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { withFormik } from 'formik';
import { Button } from '../../../../../components';
import { t } from '../../../../../i18n';
import { getCoursesCheckoutWithDiscountInformation } from '../../../../../backend-api';
import { updateCoursesCheckoutPrice } from '../../redux/actions';
import * as yup from 'yup';
import classNames from 'classnames';
import Input from './input';
import PromoCodeInput from '../promocode-input';
import { logInfo } from '../../../../../utilities/log';
import CheckoutContext from '../../checkout-context';
import './styles.scss';

const PROMOCODE_FIELD = 'promocode';
const EMAIL_FIELD = 'email';
const CONFIRM_EMAIL_FIELD = 'confirmEmail';
const NAME_FIELD = 'name';

const validationSchema = yup.object().shape({
  [EMAIL_FIELD]: yup
    .string()
    .email(t('DIGITAL_COURSES.CHECKOUT.REGISTER.INVALID_EMAIL'))
    .required(t('DIGITAL_COURSES.CHECKOUT.REGISTER.REQUIRED_FIELD')),
  [CONFIRM_EMAIL_FIELD]: yup
    .string()
    .email(t('DIGITAL_COURSES.CHECKOUT.REGISTER.INVALID_EMAIL'))
    .required(t('DIGITAL_COURSES.CHECKOUT.REGISTER.REQUIRED_FIELD'))
    .oneOf([yup.ref('email'), null], t('DIGITAL_COURSES.CHECKOUT.REGISTER.NOT_CONFIRMED_EMAIL')),
  [NAME_FIELD]: yup
    .string()
    .matches(/^[^!@#$%^&*()+=[\]\\';,./{}|":<>?]+$/, t('DIGITAL_COURSES.CHECKOUT.REGISTER.INVALID_NAME'))
    .required(t('DIGITAL_COURSES.CHECKOUT.REGISTER.REQUIRED_FIELD')),
});

const RegisterMiniForm = ({ className, courseId, values, errors, setFieldValue, handleSubmit }) => {
  const [promocode, setPromocode] = useState('');
  const [promocodeError, setPromocodeError] = useState('');
  const [isPromocodeLoading, setIsPromocodeLoading] = useState(false);
  const dispatch = useDispatch();
  const { isCoursePayment } = useContext(CheckoutContext);

  const handleApplyPromocode = async () => {
    try {
      setPromocodeError('');
      setIsPromocodeLoading(true);
      const result = await getCoursesCheckoutWithDiscountInformation(courseId, promocode);
      dispatch(updateCoursesCheckoutPrice(result.price));
    } catch (ex) {
      const serverError = ex.response?.data.error;
      const errorMessage = serverError ? t(`DIGITAL_COURSES.CHECKOUT.ERROR.${serverError}`) : t('SERVER_ERROR');
      setPromocodeError(errorMessage);
      logInfo('Courses/RegisterMiniForm', 'Promocode activation error', { courseId, promocode });
    } finally {
      setIsPromocodeLoading(false);
    }
  };

  return (
    <form className={classNames('checkout-register-mini-form', className)}>
      {isCoursePayment && (
        <div className="checkout-register-mini-form__promocode">
          <PromoCodeInput
            value={promocode}
            error={promocodeError}
            isLoading={isPromocodeLoading}
            onChange={e => setPromocode(e.target.value.toUpperCase())}
            onBlur={() => setPromocode(oldText => oldText.trim())}
            onApplyPromocode={handleApplyPromocode}
          />
        </div>
      )}
      <p>{t('DIGITAL_COURSES.CHECKOUT.REGISTER.MESSAGE')}</p>
      <Input
        value={values[EMAIL_FIELD]}
        error={errors && errors[EMAIL_FIELD]}
        onChange={newText => setFieldValue(EMAIL_FIELD, newText)}
        onBlur={() => setFieldValue(EMAIL_FIELD, values[EMAIL_FIELD].trim())}
        label={t('EMAIL')}
        name="email"
      />
      <Input
        value={values[CONFIRM_EMAIL_FIELD]}
        error={errors && errors[CONFIRM_EMAIL_FIELD]}
        onChange={newText => setFieldValue(CONFIRM_EMAIL_FIELD, newText)}
        onBlur={() => setFieldValue(CONFIRM_EMAIL_FIELD, values[CONFIRM_EMAIL_FIELD].trim())}
        label={t('CONFIRM_EMAIL')}
        name="confirmEmail"
      />
      <Input
        value={values[NAME_FIELD]}
        error={errors && errors[NAME_FIELD]}
        onChange={newText => setFieldValue(NAME_FIELD, newText)}
        label={t('NAME')}
        name="name"
      />
      <Button
        type="green"
        className="checkout-register-mini-form__button"
        disabled={Object.keys(errors).length}
        onClick={async () => {
          await setFieldValue(PROMOCODE_FIELD, promocode);
          handleSubmit();
        }}
        text={t('CONTINUE')}
      />
    </form>
  );
};

export default withFormik({
  mapPropsToValues: ({ email, name }) => ({
    [PROMOCODE_FIELD]: '',
    [NAME_FIELD]: name || '',
    [EMAIL_FIELD]: email || '',
    [CONFIRM_EMAIL_FIELD]: email || '',
  }),
  validationSchema,
  validateOnChange: true,
  validateOnBlur: true,
  handleSubmit: (values, { props: { onEnterData } }) => {
    onEnterData(values);
  },
})(RegisterMiniForm);
